import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const NAV = [
  { label: "Features", href: "#features" },
  { label: "How it works", href: "#how" },
  { label: "Strategies", href: "#strategies" },
  { label: "Pricing", href: "#pricing" },
  { label: "Knowledge Hub", href: "/knowledge-hub", external: true },
  { label: "Blog", href: "/blog", external: true },
  { label: "FAQ", href: "#faq" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    if (href.startsWith("/")) {
      window.location.href = href;
      return;
    }
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-[#e2e8f0]"
          : "bg-white/60 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 md:px-8 h-16 flex items-center justify-between">
        <a href="#top" data-testid="brand-logo" className="flex items-center gap-2.5">
          <img
            src="/tradecure-logo.png"
            alt="Tradecure"
            className="h-8 w-8 object-contain"
            width="32"
            height="32"
            loading="eager"
          />
          <span className="font-heading text-xl font-extrabold tracking-tight">
            tradecure
          </span>
          <span className="hidden sm:inline-block font-mono text-[10px] text-[#475569] bg-[#e8eef8] px-1.5 py-0.5 rounded">
            BETA
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {NAV.map((n) => (
            <button
              key={n.href}
              data-testid={`nav-${n.label.toLowerCase().replaceAll(" ", "-")}`}
              onClick={() => go(n.href)}
              className="link-underline text-sm font-semibold text-[#0f172a]/80 hover:text-[#084392] transition"
            >
              {n.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Button
            data-testid="header-cta-waitlist"
            onClick={() => go("#waitlist")}
            className="hidden sm:inline-flex bg-[#084392] hover:bg-[#06326e] text-white rounded-md h-10 px-5 font-semibold"
          >
            Join Waitlist
          </Button>
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden p-2 rounded-md border border-[#e2e8f0]"
            onClick={() => setOpen((s) => !s)}
            aria-label="Toggle menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-white border-t border-[#e2e8f0]">
          <div className="px-5 py-4 flex flex-col gap-3">
            {NAV.map((n) => (
              <button
                key={n.href}
                data-testid={`mobile-nav-${n.label.toLowerCase().replaceAll(" ", "-")}`}
                onClick={() => go(n.href)}
                className="text-left text-[#0f172a] font-semibold py-2"
              >
                {n.label}
              </button>
            ))}
            <Button
              data-testid="mobile-cta-waitlist"
              onClick={() => go("#waitlist")}
              className="bg-[#084392] hover:bg-[#06326e] text-white rounded-md h-11"
            >
              Join Waitlist
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
